/**
 * SettingsPage — the /settings route (admin only).
 *
 * Two tabs:
 *   - Integrations: the SecretsSection (8 SSM integration secrets, configured / not-configured).
 *   - Resources:    the shared ResourceDirectory (Clockify people directory + sync). Also reachable
 *                   from the standalone /resources page.
 *
 * Non-admins get an access-denied card. The underlying endpoints re-enforce admin-only server-side.
 */
import { ShieldAlert } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { SecretsSection } from '@/components/projects/SecretsSection';
import { Card, CardContent } from '@/components/ui/card';

function AccessDenied(): JSX.Element {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center gap-4 px-6 py-16 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-error/10 text-error">
          <ShieldAlert className="h-7 w-7" aria-hidden="true" />
        </div>
        <div className="space-y-1">
          <h3 className="text-lg font-semibold text-text-primary">Admin access required</h3>
          <p className="max-w-sm text-sm text-text-secondary">
            Settings — integration secrets and the resource directory — are available to
            administrators only. Contact your admin if you need a change made here.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function SettingsPage(): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold text-neutral-900">Settings</h1>
        <p className="text-sm text-text-secondary">
          Manage integration credentials and the Clockify resource directory.
        </p>
      </div>

      {!isAdmin ? (
        <AccessDenied />
      ) : (
        <SecretsSection />
      )}
    </div>
  );
}

export default SettingsPage;
